import dayjs from 'dayjs';

type DateType = Date | string | null | undefined;

/**
 * Validates if the provided start date is chronologically before the end date.
 *
 * @param startDate - The start date (can be Date or a parseable date string)
 * @param endDate   - The end date (can be Date or a parseable date string)
 * @returns boolean - True if start date is before end date, false otherwise.
 *                    Returns null if either date is invalid.
 */
export const isStartDateBeforeEndDate = (
    startDate: DateType,
    endDate: DateType
): boolean | null => {
    if (!startDate || !endDate) return null;

    const startDateObj = new Date(startDate);
    startDateObj.setHours(0, 0, 0, 0);

    const endDateObj = new Date(endDate);
    endDateObj.setHours(0, 0, 0, 0);

    return startDateObj.getTime() <= endDateObj.getTime();
};

export interface IDatesValidation {
    hasError: boolean;
    errorMessage?: string;
}

export const START_DATE_MUST_BE_GREATER = 'The end date must be greater than the start date.';

export const START_DATE_REQUIRED_IF_END_DATE_PROVIDED = 'Start date must be provided when end date is provided.';

export const START_DATE_REQUIRED = 'Start date must be provided.';

/**
 * Validates dates for start date / end date pair
 *
 * error if
 *      - start date is not provided when it's a required field
 *      - end date is provided withouth start date
 *      - start date is greater than end date
 * @param startDate - The start date (can be Date or a parseable date string)
 * @param endDate   - The end date (can be Date or a parseable date string)
 * @returns IDatesValidation {
 *      hasError - if has an error,
 *      errorMessage - human readable error description
 * }
 */
export const validateDates = (
    startDate: DateType,
    endDate: DateType,
    startDateRequired = false
): IDatesValidation => {
    // if start date required but is not provided return error
    if(startDateRequired && !startDate) {
        return {
            hasError: true,
            errorMessage: START_DATE_REQUIRED
        };
    }

    // if end date is not provided, in any case there is no error
    if(!endDate) return {
        hasError: false
    };

    // end date provided, start date is not provided - return error
    if (!startDate) return {
        hasError: true,
        errorMessage: START_DATE_REQUIRED_IF_END_DATE_PROVIDED
    };

    const startDateObj = new Date(startDate);
    startDateObj.setHours(0, 0, 0, 0);

    const endDateObj = new Date(endDate);
    endDateObj.setHours(0, 0, 0, 0);

    const endDateGreater = startDateObj.getTime() <= endDateObj.getTime();
    if (endDateGreater) return {
        hasError: false
    };

    // both provided, start date is greater - return error
    return {
        hasError: true,
        errorMessage: START_DATE_MUST_BE_GREATER
    };
};

/**
 * Formats date to the format suitable for backend
 *
 * @param date - The start date (can be dayjs.Dayjs or a parseable date string)
 * @returns date string in a YYYY-MM-DD format
 */
export const dateToBackendFormat = (date?: string | dayjs.Dayjs) => {
    if(!date) {
        return undefined;
    }
    const dayjsDate : dayjs.Dayjs = typeof date === 'string'
        ? dayjs(date)
        : date;
    if (!dayjsDate?.isValid()) return '';
    return dayjsDate.format('YYYY-MM-DD');
};

/**
 * Formats dates in frontend to be MM/DD/YYYY
 *
 * @param date - The start date (can be dayjs.Dayjs or a parseable date string)
 * @param includeTime - (optional) whether or not to include the time (HH:MM)
 * @returns date string in a MM/DD/YYYY format
 */
export const dateToFrontEndFormat = (date?: string | dayjs.Dayjs, includeTime?: boolean) => {
    if(!date) {
        return '';
    }

    const dayjsDate : dayjs.Dayjs = typeof date === 'string'
        ? dayjs(date)
        : date;

    if (!dayjsDate?.isValid()) return '';

    return includeTime
        ? dayjsDate.format('MM/DD/YYYY HH:mm')
        : dayjsDate.format('MM/DD/YYYY');
};


/**
 * @param startDate - The start date (dayjs.Dayjs)
 * @param endDate   - The end date (dayjs.Dayjs)
 * @returns boolean - True if start date is before end date, false otherwise
 */
export const datesAreSequential = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    return endDate.isSame(startDate, 'day') || endDate.isAfter(startDate, 'day');
};

/**
 * @param startDateString - The start date string (can be either in YYYY-MM-DD or MM/DD/YYYY format)
 * @param endDateString   - The end date string (can be either in YYYY-MM-DD or MM/DD/YYYY format)
 * @returns boolean - True, if dates pair is active - current date is between them
 */
export const isActiveDatesPair = (startDateString?: string, endDateString?: string) => {
    const today = dayjs();
    const startDate = startDateString ? dayjs(startDateString) : null;
    const endDate = endDateString ? dayjs(endDateString) : null;
    if(!endDate && !startDate) {
        return true;
    }
    if(!startDate) {
        return true;
    }
    if(!endDate) {
        return datesAreSequential(startDate, today);
    }
    return datesAreSequential(startDate, today) && datesAreSequential(today, endDate);
};

/**
 * @param date - Date string (can be either in YYYY-MM-DD or MM/DD/YYYY format)
 * @returns boolean - True, if passed date comes after today's date
 */
export const isFutureDate = (date: DateType): boolean => {
    const today = dayjs();
    const checkDate = dayjs(date);
    return checkDate.isAfter(today);
};

/**
 * @param date - Date string (can be either in YYYY-MM-DD or MM/DD/YYYY format)
 * @returns boolean - True, if passed date comes before today's date
 */
export const isPastDate = (date: DateType): boolean => {
    const today = dayjs();
    const checkDate = dayjs(date);
    return checkDate.isBefore(today);
};

/**
 * @param date - Date to be converted to EST
 * @returns string - the input date as EST string
 */
export const toESTDateString = function (date: Date): string {
    return date.toLocaleString('en-US', { timeZone: 'America/New_York' });
};
